<template>
  <div
    class="login-showcase"
    :style="{ backgroundColor: activeSlide.background }"
    @mouseenter="pause"
    @mouseleave="resume"
  >
    <div class="login-showcase__header">
      <Transition name="login-showcase-fade" mode="out-in">
        <div :key="activeSlide.key">
          <h2 class="login-showcase__title">{{ $t(activeSlide.title) }}</h2>
          <p class="login-showcase__description">{{ $t(activeSlide.description) }}</p>
        </div>
      </Transition>

      <div class="login-showcase__dots">
        <button
          v-for="(slide, index) in slides"
          :key="slide.key"
          type="button"
          class="login-showcase__dot"
          :class="{ 'login-showcase__dot--active': index === activeIndex }"
          :aria-label="$t(slide.title)"
          @click="goTo(index)"
        ></button>
      </div>
    </div>

    <div class="login-showcase__stage">
      <Transition name="login-showcase-fade">
        <div
          :key="activeSlide.key"
          class="login-showcase__slide"
          :class="`login-showcase__slide--${activeSlide.layout}`"
        >
          <component :is="activeSlide.component" />
        </div>
      </Transition>
    </div>
  </div>
</template>

<script>
  import { ref, computed, onMounted, onBeforeUnmount, markRaw } from 'vue';

  import ConfiguratorVisual from './ConfiguratorVisual.vue';
  import PipelineVisual from './PipelineVisual.vue';
  import PlanningVisual from './PlanningVisual.vue';
  import ProFormaVisual from './ProFormaVisual.vue';
  import ProposalsVisual from './ProposalsVisual.vue';
  import WorkOrderVisual from './WorkOrderVisual.vue';

  const ROTATION_INTERVAL = 7000;

  const createSlide = (key, component, background, layout = 'center') => ({
    key,
    component: markRaw(component),
    background,
    layout,
    title: `auth.showcase_${key}_title`,
    description: `auth.showcase_${key}_description`,
  });

  export default {
    name: 'LoginShowcase',
    setup() {
      const slides = [
        createSlide('configurator', ConfiguratorVisual, '#e6ecff'),
        createSlide('pipeline', PipelineVisual, '#e9eaee'),
        createSlide('proposals', ProposalsVisual, '#f0e8ff', 'bottom'),
        createSlide('planning', PlanningVisual, '#e3f5ec'),
        createSlide('pro_forma', ProFormaVisual, '#f0e8ff'),
        createSlide('work_order', WorkOrderVisual, '#fff0dd'),
      ];

      const activeIndex = ref(0);
      const activeSlide = computed(() => slides[activeIndex.value]);
      let timer = null;
      let isHovered = false;

      const stopRotation = () => {
        clearInterval(timer);
        timer = null;
      };

      const startRotation = () => {
        stopRotation();
        if (isHovered || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        timer = setInterval(() => {
          activeIndex.value = (activeIndex.value + 1) % slides.length;
        }, ROTATION_INTERVAL);
      };

      const pause = () => {
        isHovered = true;
        stopRotation();
      };

      const resume = () => {
        isHovered = false;
        startRotation();
      };

      const goTo = (index) => {
        activeIndex.value = index;
        startRotation();
      };

      onMounted(startRotation);
      onBeforeUnmount(stopRotation);

      return { slides, activeIndex, activeSlide, goTo, pause, resume };
    },
  };
</script>

<style scoped lang="scss">
  .login-showcase {
    --showcase-ink: #000921;
    --showcase-text: #434f6f;
    --showcase-muted: #8c95ad;
    --showcase-stroke: #e3e6ee;
    --showcase-stroke-button: #d7d7d7;
    --showcase-primary: #3364e6;
    --showcase-primary-tint: #f5f8ff;
    --showcase-blue-bg: #e6ecff;
    --showcase-green-bg: #e3f5ec;
    --showcase-green: #007a4b;
    --showcase-purple-bg: #f0e8ff;

    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: hidden;
    border-radius: 1.5rem;
    transition: background-color 0.6s ease;

    &__header {
      padding: 3.5rem 3.5rem 0;
    }

    &__title {
      max-width: 440px;
      color: var(--showcase-ink);
      font-size: 30px;
      font-weight: 500;
      line-height: 1.15;
      letter-spacing: -0.6px;
    }

    &__description {
      max-width: 440px;
      margin-top: 1rem;
      color: var(--showcase-text);
      font-size: 17px;
      line-height: 1.6;
    }

    &__dots {
      display: flex;
      margin: 1rem 0 0 -5px;
    }

    &__dot {
      padding: 8px 3px;
      border: 0;
      background: none;
      cursor: pointer;
      &::before {
        content: '';
        display: block;
        width: 8px;
        height: 8px;
        border-radius: 4px;
        background-color: rgba(0, 9, 33, 0.15);
        transition:
          width 0.3s ease,
          background-color 0.3s ease;
      }
      @include hover {
        &::before {
          background-color: rgba(0, 9, 33, 0.3);
        }
      }
      &--active::before,
      &--active:hover::before {
        width: 24px;
        background-color: var(--showcase-primary);
      }
    }

    &__stage {
      position: relative;
      flex: 1;
      min-height: 420px;
    }

    &__slide {
      position: absolute;
      inset: 0;
      display: flex;
      &--center {
        align-items: center;
        justify-content: center;
        padding: 2rem 3.5rem 3.5rem;
      }
      &--bottom {
        align-items: flex-end;
        padding: 2.5rem 0 0 3.5rem;
      }
    }
  }

  .login-showcase-fade-enter-active,
  .login-showcase-fade-leave-active {
    transition:
      opacity 0.4s ease,
      transform 0.4s ease;
  }

  .login-showcase-fade-enter-from {
    opacity: 0;
    transform: translateY(8px);
  }

  .login-showcase-fade-leave-to {
    opacity: 0;
  }
</style>
